import { Dispatch, SetStateAction, useMemo } from 'react';

import {
  AssessmentChartCard as AssessmentChartCardLegacy,
  ChakraBox,
} from '@equip.health/ui';
import {
  AssessmentChartCard as AssessmentChartCardRevamp,
  AssessmentListCard,
} from '~/components/maud/assessment-card';
import type {
  AssessmentChartCardProps,
  CompleteTogetherBannerVariant,
} from '~/components/maud/assessment-card';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { AssessmentEvents } from '~/analytics';
import { AssessmentChartDataScoreStrategyMap } from '~/lib/constants/survey/AssessmentChartDataScoreStrategyMap';
import { SurveyNameToAbbreviationMap } from '~/lib/constants/survey/SurveyNameToAbbreviationMap';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToChartPrimaryKeyMap } from '~/lib/constants/survey/SurveyNameToChartPrimaryKeyMap';
import { SurveyNameToChartYAxisDomainMap } from '~/lib/constants/survey/SurveyNameToChartYAxisMaxMap';
import { SurveyNameToListCardTitlesMap } from '~/lib/constants/survey/SurveyNameToListCardTitlesMap';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useComponentPortalContext } from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';
import {
  AssessmentTimepointContext,
  buildAssessmentHighlightZones,
  buildAssessmentTimelineMarkers,
  filterGroupedAssessmentToCurrentEpisode,
} from '~/lib/util/assessmentTimepoint.util';
import {
  deriveChartData,
  getAssessmentListCardItems,
} from '~/lib/util/groupedAssessment.util';
import {
  onContinueSurvey,
  onDownloadSurveyPdf,
} from '~/lib/util/surveyActions.util';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import {
  AssessmentEventAttribute,
  AssessmentEventInteractionType,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';

type Props = {
  assessmentTimepointContext?: AssessmentTimepointContext;
  groupedAssessment: GroupedAssessment;
  patientPreferredFullName: string;
  // @deprecated legacy view assessment modal
  setViewAssessmentModalData: Dispatch<SetStateAction<SurveyDetail | null>>;
  setDeleteAssessmentModalAssessmentId: Dispatch<SetStateAction<string>>;
  onCreateAssessment: (surveyName: SurveyName, jsonParameters: string) => void;
};

const TOP_LEVEL_CARD_ID = 'assessment-card';

// Mean-scored assessments whose published scoring manuals report decimal
// values (0-6 or 0-5 scales). All other chart assessments are integer-sum
// per clinical convention and render their scores at stored precision.
const ASSESSMENTS_WITH_DECIMAL_SCORES = new Set<SurveyName>([
  SurveyName.EDE_Q,
  SurveyName.PARDI_ARQ_SELF,
  SurveyName.PARDI_ARQ_PARENT,
  SurveyName.CET,
]);

// Surveys whose card action menu drops "Create new" — providers can still
// View past submissions, Resume in-progress ones, and Delete, but new
// instances can't be spawned. Applied only when `phq9Enabled` is on, i.e.
// PHQ-9 has launched as the current mood measure; while the flag is off,
// PHQ-8 keeps Create new. Per GX-3508 / EQ-20527.
const SURVEYS_WITH_HIDDEN_CREATE_ACTIONS = new Set<SurveyName>([SurveyName.PHQ8]);

export const GroupedAssessmentCard = ({
  assessmentTimepointContext,
  groupedAssessment,
  patientPreferredFullName,
  onCreateAssessment,
  setDeleteAssessmentModalAssessmentId,
  setViewAssessmentModalData,
}: Props) => {
  const { currentPatientGender: patientGender } = useGlobalContext();
  const { setComponentVisibility } = useComponentPortalContext();
  const {
    assessmentTabUiRevamp: isAssessmentTabUiRevampEnabled,
    assessmentRedesignHistoryPage: isAssessmentRedesignHistoryPageEnabled,
    phq9Enabled: isPhq9Enabled
  } = useFlags<LDFlags>();
  const { trackEvent } = useAnalytics();

  const frequency = JSON.parse(groupedAssessment.jsonParameters).frequency;
  const abbreviation = SurveyNameToAbbreviationMap[groupedAssessment.name];
  const assessmentDisplayName =
    isAssessmentTabUiRevampEnabled && abbreviation
      ? `${groupedAssessment.displayName} (${abbreviation})`
      : groupedAssessment.displayName;
  const isChartAssessment =
    !!AssessmentChartDataScoreStrategyMap[groupedAssessment.name];
  const hideCreateActions =
    isPhq9Enabled && SURVEYS_WITH_HIDDEN_CREATE_ACTIONS.has(groupedAssessment.name);
  const chartGroupedAssessment = useMemo(
    () =>
      isAssessmentTabUiRevampEnabled && isChartAssessment
        ? filterGroupedAssessmentToCurrentEpisode(
            groupedAssessment,
            assessmentTimepointContext?.currentEpisode,
          )
        : groupedAssessment,
    [
      assessmentTimepointContext?.currentEpisode,
      groupedAssessment,
      isAssessmentTabUiRevampEnabled,
      isChartAssessment,
    ],
  );
  const highlightZones = useMemo(
    () =>
      isAssessmentTabUiRevampEnabled &&
      groupedAssessment.isCore &&
      assessmentTimepointContext
        ? buildAssessmentHighlightZones({
            ...assessmentTimepointContext,
            groupedAssessment: chartGroupedAssessment,
          })
        : undefined,
    [
      assessmentTimepointContext,
      chartGroupedAssessment,
      groupedAssessment.isCore,
      isAssessmentTabUiRevampEnabled,
    ],
  );
  const shadingLegendEntries = useMemo<
    AssessmentChartCardProps['shadingLegendEntries']
  >(() => {
    if (
      !isAssessmentTabUiRevampEnabled ||
      !groupedAssessment.isCore ||
      !assessmentTimepointContext
    ) {
      return undefined;
    }
    const hasOverdue = highlightZones?.some(
      (zone) => zone.variant === 'assessment-overdue',
    );
    const hasCurrent = highlightZones?.some(
      (zone) => zone.variant === 'assessment-current',
    );
    const hasNext = highlightZones?.some(
      (zone) => zone.variant === 'assessment-next',
    );
    const entries: NonNullable<
      AssessmentChartCardProps['shadingLegendEntries']
    > = [];
    if (hasOverdue) {
      entries.push({ label: 'Overdue', variant: 'assessment-overdue' });
    } else if (hasCurrent) {
      entries.push({
        label: 'Current assessment',
        variant: 'assessment-current',
      });
    }
    if (hasNext) {
      entries.push({ label: 'Next assessment', variant: 'assessment-next' });
    }
    return entries.length > 0 ? entries : undefined;
  }, [
    assessmentTimepointContext,
    groupedAssessment.isCore,
    highlightZones,
    isAssessmentTabUiRevampEnabled,
  ]);

  const timelineMarkers = useMemo(
    () =>
      isAssessmentTabUiRevampEnabled && assessmentTimepointContext
        ? buildAssessmentTimelineMarkers(assessmentTimepointContext)
        : undefined,
    [assessmentTimepointContext, isAssessmentTabUiRevampEnabled],
  );

  const chartData = useMemo(
    () =>
      isChartAssessment
        ? deriveChartData(chartGroupedAssessment, patientGender)
        : [],
    [chartGroupedAssessment, isChartAssessment, patientGender],
  );
  const chartDomain = isAssessmentTabUiRevampEnabled
    ? assessmentTimepointContext?.chartDomain
    : undefined;
  const yAxisDomain = isAssessmentTabUiRevampEnabled
    ? SurveyNameToChartYAxisDomainMap[groupedAssessment.name]
    : undefined;

  const baseAssessmentEventProperties = {
    [AssessmentEventAttribute.SurveyId]: groupedAssessment.externalId,
    [AssessmentEventAttribute.SurveyName]: groupedAssessment.name,
    [AssessmentEventAttribute.Source]: AssessmentEventSource.AssessmentCards,
  };

  const handleCompleteTogetherClick = (
    assessmentResponseId: string,
    bannerVariant?: CompleteTogetherBannerVariant,
  ) => {
    const event =
      bannerVariant === 'yellow'
        ? AssessmentEvents.completeTogetherAssessmentCardApproachingDueButtonClicked
        : AssessmentEvents.completeTogetherAssessmentCardOverdueButtonClicked;
    trackEvent(event, {
      ...baseAssessmentEventProperties,
      [AssessmentEventAttribute.ResponseExternalId]: assessmentResponseId,
    });
    onContinueSurvey(
      assessmentResponseId,
      groupedAssessment.assessments,
      setComponentVisibility,
    );
  };

  const sharedChartCardProps = {
    assessmentName: assessmentDisplayName,
    avatarTooltipWrapper: ({
      children,
      providerExternalId,
    }: {
      children: React.ReactNode;
      providerExternalId: string;
    }) => (
      <ProviderAvatarTooltipWrapper
        placement="bottom"
        providerExternalId={providerExternalId}
      >
        {children}
      </ProviderAvatarTooltipWrapper>
    ),
    chartData,
    descriptionText: groupedAssessment.description,
    frequency,
    id: groupedAssessment.externalId,
    isAssessmentActive: groupedAssessment.status === 'Active',
    isHistoryEnabled: isAssessmentRedesignHistoryPageEnabled,
    onClickCreateNew: (source: 'title' | 'kebab') => {
      trackEvent(AssessmentEvents.createNewAssessmentClicked, {
        [AssessmentEventAttribute.SurveyName]: groupedAssessment.name,
        [AssessmentEventAttribute.Source]:
          source === 'title'
            ? AssessmentEventSource.AssessmentCardsTitleButton
            : AssessmentEventSource.AssessmentCardTitleOptionsMenu,
      });
      onCreateAssessment(
        groupedAssessment.name,
        groupedAssessment.jsonParameters,
      );
    },
    onDeleteClick: (assessmentResponseId: string) => {
      trackEvent(
        AssessmentEvents.deleteAssessmentListItemButtonClicked,
        baseAssessmentEventProperties,
      );
      setDeleteAssessmentModalAssessmentId(assessmentResponseId);
    },
    onResumeClick: (assessmentResponseId: string) => {
      trackEvent(AssessmentEvents.resumeAssessmentClicked, {
        ...baseAssessmentEventProperties,
        [AssessmentEventAttribute.Source]:
          AssessmentEventSource.AssessmentCardTitle,
      });
      onContinueSurvey(
        assessmentResponseId,
        groupedAssessment.assessments,
        setComponentVisibility,
      );
    },
    onViewAssessmentClick: (assessmentResponseId: string) => {
      trackEvent(AssessmentEvents.assessmentViewClicked, {
        ...baseAssessmentEventProperties,
        [AssessmentEventAttribute.Source]:
          AssessmentEventSource.AssessmentCardTitleOptionsMenu,
      });
      setViewAssessmentModalData({
        externalId: assessmentResponseId,
        surveyName: groupedAssessment.name,
      });
    },
    onViewHistoryClick: () => {},
    primaryScoreKey: SurveyNameToChartPrimaryKeyMap[groupedAssessment.name],
    hideCreateActions,
  };

  const handleChartLineClick = (
    lineName: string,
    assessmentResponseId: string,
  ) => {
    trackEvent(AssessmentEvents.assessmentChartLineInteraction, {
      ...baseAssessmentEventProperties,
      [AssessmentEventAttribute.InteractionType]:
        AssessmentEventInteractionType.Click,
      [AssessmentEventAttribute.LineName]: lineName,
    });
    trackEvent(AssessmentEvents.assessmentViewClicked, {
      ...baseAssessmentEventProperties,
      [AssessmentEventAttribute.Source]:
        AssessmentEventSource.AssessmentChartDataPoint,
    });
    setViewAssessmentModalData({
      externalId: assessmentResponseId,
      surveyName: groupedAssessment.name,
    });
  };

  const handleChartLineHoverEnd = (lineName: string, dwellMs: number) => {
    trackEvent(AssessmentEvents.assessmentChartLineInteraction, {
      ...baseAssessmentEventProperties,
      [AssessmentEventAttribute.InteractionType]:
        AssessmentEventInteractionType.Hover,
      [AssessmentEventAttribute.LineName]: lineName,
      [AssessmentEventAttribute.DwellMs]: dwellMs,
    });
  };

  const handleTooltipDwellEnd = (dwellMs: number) => {
    trackEvent(AssessmentEvents.assessmentChartScoreTooltipInteraction, {
      ...baseAssessmentEventProperties,
      [AssessmentEventAttribute.InteractionType]:
        AssessmentEventInteractionType.Hover,
      [AssessmentEventAttribute.DwellMs]: dwellMs,
    });
  };

  return (
    <ChakraBox // needed for scrolling to this card when clicking the index menu
      id={`${TOP_LEVEL_CARD_ID}__${groupedAssessment.externalId}`}
      width="100%"
    >
      {isChartAssessment && isAssessmentTabUiRevampEnabled && (
        <AssessmentChartCardRevamp
          {...sharedChartCardProps}
          chartHeight={340}
          dashedMissingBridge={false}
          highlightZones={highlightZones}
          hoverFocusDim
          monthlyXAxisTicks
          onCompleteTogetherClick={handleCompleteTogetherClick}
          onLineClick={handleChartLineClick}
          onLineHoverEnd={handleChartLineHoverEnd}
          onTooltipDwellEnd={handleTooltipDwellEnd}
          padScoresToTwoDecimals={ASSESSMENTS_WITH_DECIMAL_SCORES.has(
            groupedAssessment.name,
          )}
          shadingLegendEntries={shadingLegendEntries}
          showHoverVerticalLine={false}
          showTodayMarker
          solidHorizontalGrid
          timelineMarkers={timelineMarkers}
          xAxisDomain={chartDomain}
          xAxisLabelFormat="monthYear"
          yAxisMax={yAxisDomain?.max}
          yAxisMin={yAxisDomain?.min}
        />
      )}
      {isChartAssessment && !isAssessmentTabUiRevampEnabled && (
        <AssessmentChartCardLegacy
          {...sharedChartCardProps}
          onClickCreateNew={() => sharedChartCardProps.onClickCreateNew('title')}
          onCompleteTogetherClick={(assessmentResponseId: string) =>
            handleCompleteTogetherClick(assessmentResponseId)
          }
        />
      )}
      {!isChartAssessment && (
        <AssessmentListCard
          assessmentName={assessmentDisplayName}
          assessmentsResponses={getAssessmentListCardItems(groupedAssessment)}
          avatarTooltipWrapper={({ children, providerExternalId }) => (
            <ProviderAvatarTooltipWrapper
              placement="bottom"
              providerExternalId={providerExternalId}
            >
              {children}
            </ProviderAvatarTooltipWrapper>
          )}
          descriptionText={groupedAssessment.description}
          frequency={frequency}
          id={groupedAssessment.externalId}
          isAssessmentActive={groupedAssessment.status === 'Active'}
          isHistoryEnabled={isAssessmentRedesignHistoryPageEnabled}
          onCardHeaderResumeClick={(assessmentResponseId: string) => {
            trackEvent(AssessmentEvents.resumeAssessmentClicked, {
              ...baseAssessmentEventProperties,
              [AssessmentEventAttribute.Source]:
                AssessmentEventSource.AssessmentCardTitle,
            });
            onContinueSurvey(
              assessmentResponseId,
              groupedAssessment.assessments,
              setComponentVisibility,
            );
          }}
          onCompleteTogetherClick={handleCompleteTogetherClick}
          onCreateNewClick={(source: 'title' | 'kebab') => {
            trackEvent(AssessmentEvents.createNewAssessmentClicked, {
              [AssessmentEventAttribute.SurveyName]: groupedAssessment.name,
              [AssessmentEventAttribute.Source]:
                source === 'title'
                  ? AssessmentEventSource.AssessmentCardsTitleButton
                  : AssessmentEventSource.AssessmentCardTitleOptionsMenu,
            });
            onCreateAssessment(
              groupedAssessment.name,
              groupedAssessment.jsonParameters,
            );
          }}
          onDeleteClick={(assessmentResponseId: string) => {
            trackEvent(
              AssessmentEvents.deleteAssessmentListItemButtonClicked,
              baseAssessmentEventProperties,
            );
            setDeleteAssessmentModalAssessmentId(assessmentResponseId);
          }}
          onDownloadClick={(assessmentResponseId: string) => {
            onDownloadSurveyPdf(
              assessmentResponseId,
              groupedAssessment.assessments,
              patientPreferredFullName,
            );
          }}
          onListItemResumeClick={(assessmentResponseId: string) => {
            trackEvent(AssessmentEvents.resumeAssessmentClicked, {
              ...baseAssessmentEventProperties,
              [AssessmentEventAttribute.Source]:
                AssessmentEventSource.AssessmentListItem,
            });
            onContinueSurvey(
              assessmentResponseId,
              groupedAssessment.assessments,
              setComponentVisibility,
            );
          }}
          onHeaderViewClick={(assessmentResponseId: string) => {
            trackEvent(AssessmentEvents.assessmentViewClicked, {
              ...baseAssessmentEventProperties,
              [AssessmentEventAttribute.Source]:
                AssessmentEventSource.AssessmentCardTitleOptionsMenu,
            });
            setViewAssessmentModalData({
              externalId: assessmentResponseId,
              surveyName: groupedAssessment.name,
            });
          }}
          onListItemViewClick={(assessmentResponseId: string) => {
            trackEvent(AssessmentEvents.assessmentViewClicked, {
              ...baseAssessmentEventProperties,
              [AssessmentEventAttribute.Source]:
                AssessmentEventSource.AssessmentListItem,
            });
            setViewAssessmentModalData({
              externalId: assessmentResponseId,
              surveyName: groupedAssessment.name,
            });
          }}
          onViewHistoryClick={() => {}}
          titleRow={SurveyNameToListCardTitlesMap[groupedAssessment.name]}
        />
      )}
    </ChakraBox>
  );
};
